'use client';
import { useEngineerIdentity } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type ChangeEvent, type ReactNode, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { InputField } from '@/components/ui/input-field';
import { isMorfiMarketsEmail } from '@/config/operator-dashboard';
import { useNetworkConfig } from '@/lib/network/use-network-config';
import { logger } from '@/lib/telemetry/logger';

export function FundingSandboxCustomer(props: {
  environment: string | undefined;
  disabled: boolean;
  error?: boolean;
  onAuthenticate(email: string): Promise<void>;
}): ReactNode {
  const { user, isAuthenticated } = useEngineerIdentity();
  const { network } = useNetworkConfig();
  if (
    process.env.NODE_ENV !== 'development' ||
    (network !== 'devnet' && network !== 'local') ||
    props.environment !== 'sandbox' ||
    !isAuthenticated ||
    !isMorfiMarketsEmail(user?.email)
  )
    return null;
  return <SandboxCustomerForm {...props} />;
}

function SandboxCustomerForm({
  disabled,
  error,
  onAuthenticate,
}: Parameters<typeof FundingSandboxCustomer>[0]): ReactNode {
  const t = useTranslations('onramp.devtools');
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const submitting = useRef(false);
  const valid = /^test-[a-z0-9_-]+@moonpay\.test$/i.test(email.trim());
  async function authenticate(): Promise<void> {
    if (disabled || submitting.current || !valid) return;
    submitting.current = true;
    setPending(true);
    setFailed(false);
    try {
      await onAuthenticate(email.trim());
    } catch {
      setFailed(true);
      logger.warn('onramp.devtools_customer_session_failed');
    }
    submitting.current = false;
    setPending(false);
  }
  return (
    <section
      className="flex flex-col gap-3 rounded-control bg-background-tertiary p-4"
      data-private="true"
    >
      <h2 className="text-heading-sm">{t('customer_title')}</h2>
      <p className="text-caption text-foreground-secondary">{t('customer_description')}</p>
      <InputField
        label={t('customer_email')}
        type="email"
        autoComplete="off"
        value={email}
        disabled={disabled || pending}
        onChange={(event: ChangeEvent<HTMLInputElement>): void => setEmail(event.target.value)}
      />
      {(failed || error) && (
        <p role="alert" className="text-caption text-foreground-secondary">
          {t('customer_failed')}
        </p>
      )}
      <AnimatedButton
        disabled={disabled || pending || !valid}
        loading={pending}
        onClick={(): void => {
          void authenticate();
        }}
      >
        {t('customer_connect')}
      </AnimatedButton>
    </section>
  );
}
